import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  ListItemIcon,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
} from '@mui/material';
import logo from '../assets/logo-2.png';
import logoFallback from '../assets/nexus-logo-transparent.png';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings as SettingsIcon,
  NotificationsActive,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useClients } from '../contexts/ClientContext';
import Chatbot from './Chatbot';
import SearchDialog from './SearchDialog';
import NotificationsDialog from './NotificationsDialog';
import LegalFooterLinks from './LegalFooterLinks';
import NavSidebar from './NavSidebar';
import { ClientSelector } from './primitives';
import {
  NAV_DRAWER_WIDTH,
  NAV_RAIL_COLLAPSED_WIDTH,
  getActiveNavTitle,
} from '../config/navGroups';
import { dashboardAPI } from '../services/api';
import {
  parseClientIdParam,
  resolveScopedClientId,
} from '../utils/lastSelectedClientStorage';
import {
  isGlobalClientSelectorRoute,
  shouldSyncClientIdInUrl,
} from '../utils/scopedClientRoutes';

const DISK_POLL_MS = 5 * 60 * 1000;
const DISK_NOTIFY_COOLDOWN_MS = 6 * 60 * 60 * 1000;

const drawerWidth = NAV_DRAWER_WIDTH;
const railWidth = NAV_RAIL_COLLAPSED_WIDTH;

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navExpanded, setNavExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { unreadCount, addSystemNotification } = useNotifications();
  const {
    clients,
    loaded: clientsLoaded,
    lastSelectedClientId,
    setLastSelectedClientId,
  } = useClients();
  const [storageStatus, setStorageStatus] = useState(null);

  const showGlobalClientSelector =
    clientsLoaded &&
    Array.isArray(clients) &&
    clients.length > 0 &&
    isGlobalClientSelectorRoute(location.pathname);

  const urlClientId = parseClientIdParam(searchParams.get('client_id'));
  const globalClientId = resolveScopedClientId({
    clients,
    urlClientId,
    persistedClientId: lastSelectedClientId,
  });

  const handleGlobalClientChange = (client) => {
    if (!client?.id) return;
    setLastSelectedClientId(client.id);
    if (shouldSyncClientIdInUrl(location.pathname)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('client_id', String(client.id));
          return next;
        },
        { replace: true },
      );
    }
  };

  useEffect(() => {
    if (!clientsLoaded || urlClientId == null) return;
    if (clients.some((c) => c.id === urlClientId)) {
      setLastSelectedClientId(urlClientId);
    }
  }, [clientsLoaded, urlClientId, clients, setLastSelectedClientId]);

  // Phase 16.R5: collapse hover-expand rail on route change (mouse often still over drawer).
  useEffect(() => {
    setNavExpanded(false);
  }, [location.pathname, location.search]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleSearchOpen = () => {
    setSearchOpen(true);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
  };

  const handleNotificationsOpen = () => {
    setNotificationsOpen(true);
  };

  const handleNotificationsClose = () => {
    setNotificationsOpen(false);
  };

  // Keyboard shortcut for search (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchOpen]);

  useEffect(() => {
    if (!user) {
      setStorageStatus(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await dashboardAPI.getStorageStatus();
        if (cancelled) return;
        setStorageStatus(data);
        if (
          !data.enabled ||
          data.level === 'ok' ||
          data.error ||
          typeof sessionStorage === 'undefined'
        ) {
          if (data.level === 'ok') {
            sessionStorage.removeItem('disk_alert_warning');
            sessionStorage.removeItem('disk_alert_critical');
          }
          return;
        }
        const key = `disk_alert_${data.level}`;
        const last = parseInt(sessionStorage.getItem(key) || '0', 10);
        const now = Date.now();
        if (now - last < DISK_NOTIFY_COOLDOWN_MS) return;
        const gbFree = (data.free_bytes / 1024 ** 3).toFixed(1);
        const msg =
          data.level === 'critical'
            ? `Server disk is ${data.used_percent}% full (${gbFree} GiB free on ${data.checked_path}). Free space or expand storage soon.`
            : `Server disk is ${data.used_percent}% full (${gbFree} GiB free). Consider cleanup or expanding storage.`;
        addSystemNotification('maintenance', msg, {
          disk: true,
          level: data.level,
          path: data.checked_path,
        });
        sessionStorage.setItem(key, String(now));
      } catch {
        if (!cancelled) setStorageStatus(null);
      }
    };
    poll();
    const id = setInterval(poll, DISK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // addSystemNotification is stable enough for polling; avoid resetting interval each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleNavNavigate = (path) => {
    setNavExpanded(false);
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const pageTitle = getActiveNavTitle({
    pathname: location.pathname,
    search: location.search,
  });

  const renderDrawer = (collapsed) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: collapsed ? 1.5 : 3,
          minHeight: 96,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(48, 54, 61, 0.5)',
          background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'linear-gradient(45deg, rgba(88, 166, 255, 0.05) 0%, transparent 50%, rgba(247, 129, 102, 0.05) 100%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          component="img"
          src={logo}
          onError={(e) => {
            if (e.target.src !== logoFallback) e.target.src = logoFallback;
          }}
          alt="Dynamic Nexus"
          sx={{
            height: collapsed ? 40 : 88,
            width: 'auto',
            objectFit: 'contain',
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            transition: 'height 0.2s ease',
          }}
        />
      </Box>

      <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>
        <NavSidebar
          isMobile={isMobile}
          onNavigate={handleNavNavigate}
          collapsed={collapsed}
        />
      </Box>

      <Box sx={{ p: collapsed ? 1 : 2, borderTop: '1px solid rgba(48, 54, 61, 0.5)' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 2,
            p: collapsed ? 1 : 2.5,
            borderRadius: 3,
            background: collapsed
              ? 'transparent'
              : 'linear-gradient(135deg, #21262d 0%, #161b22 100%)',
            border: collapsed
              ? '1px solid transparent'
              : '1px solid rgba(48, 54, 61, 0.5)',
          }}
        >
          <Avatar
            src={user?.profile_image || null}
            sx={{
              width: 44,
              height: 44,
              background: user?.profile_image
                ? 'transparent'
                : 'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
              fontSize: '1.2rem',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {user?.first_name?.charAt(0)?.toUpperCase() ||
              user?.username?.charAt(0)?.toUpperCase() ||
              'U'}
          </Avatar>
          {!collapsed && (
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: '#f0f6fc',
                  fontSize: '0.95rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.username || 'User'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#8b949e',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.role || 'Administrator'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${railWidth}px)` },
          ml: { md: `${railWidth}px` },
          backgroundColor: 'rgba(22, 27, 34, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(48, 54, 61, 0.5)',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 1, sm: 3 },
            gap: 1,
            minHeight: { xs: 56, sm: 64 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              flex: { xs: '1 1 auto', sm: '0 1 auto' },
              minWidth: 0,
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: { xs: 0, sm: 2 },
                display: { md: 'none' },
                backgroundColor: 'rgba(88, 166, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(88, 166, 255, 0.2)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                color: '#f0f6fc',
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                letterSpacing: '-0.02em',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {pageTitle}
            </Typography>
            {showGlobalClientSelector && (
              <ClientSelector
                clients={clients}
                value={globalClientId}
                onChange={handleGlobalClientChange}
                label="Client"
                minWidth={0}
                sx={{
                  ml: { xs: 0, sm: 1 },
                  flex: { xs: '1 1 120px', sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 160 },
                  maxWidth: { xs: '100%', sm: 220 },
                  '& .MuiInputLabel-root': { color: '#8b949e' },
                  '& .MuiOutlinedInput-root': {
                    color: '#f0f6fc',
                    bgcolor: 'rgba(13, 17, 23, 0.6)',
                    '& fieldset': { borderColor: '#30363d' },
                    '&:hover fieldset': { borderColor: '#58a6ff' },
                  },
                  '& .MuiSvgIcon-root': { color: '#8b949e' },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Search (Ctrl+K)" arrow>
              <IconButton
                size="large"
                onClick={handleSearchOpen}
                sx={{
                  color: '#8b949e',
                  backgroundColor: 'rgba(88, 166, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(88, 166, 255, 0.2)',
                    color: '#f0f6fc',
                  },
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notifications" arrow>
              <IconButton
                size="large"
                onClick={handleNotificationsOpen}
                sx={{
                  color: '#8b949e',
                  backgroundColor: 'rgba(88, 166, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(88, 166, 255, 0.2)',
                    color: '#f0f6fc',
                  },
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsActive />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Profile" arrow>
              <IconButton
                size="large"
                onClick={handleProfileMenuOpen}
                sx={{
                  color: '#8b949e',
                  backgroundColor: 'rgba(88, 166, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(88, 166, 255, 0.2)',
                    color: '#f0f6fc',
                  },
                }}
              >
                <AccountCircle />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: railWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(180deg, #161b22 0%, #21262d 100%)',
              borderRight: '1px solid rgba(48, 54, 61, 0.5)',
              backdropFilter: 'blur(20px)',
            },
          }}
        >
          {renderDrawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          onMouseEnter={() => setNavExpanded(true)}
          onMouseLeave={() => setNavExpanded(false)}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: navExpanded ? drawerWidth : railWidth,
              overflowX: 'hidden',
              transition: 'width 0.2s ease',
              background: 'linear-gradient(180deg, #161b22 0%, #21262d 100%)',
              borderRight: '1px solid rgba(48, 54, 61, 0.5)',
              backdropFilter: 'blur(20px)',
              boxShadow: navExpanded
                ? '4px 0 24px rgba(0, 0, 0, 0.35)'
                : 'none',
              zIndex: (t) => t.zIndex.drawer + 1,
            },
          }}
          open
        >
          {renderDrawer(!navExpanded)}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${railWidth}px)` },
          background: 'linear-gradient(135deg, #0d1117 0%, #161b22 100%)',
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 80%, rgba(88, 166, 255, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(247, 129, 102, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Toolbar /> {/* Spacer for fixed AppBar */}
        {storageStatus?.enabled &&
          storageStatus.level !== 'ok' &&
          !storageStatus.error && (
            <Alert
              severity={
                storageStatus.level === 'critical' ? 'error' : 'warning'
              }
              sx={{
                mx: { xs: 2, sm: 3, md: 4 },
                mt: 2,
                border: '1px solid',
                borderColor:
                  storageStatus.level === 'critical'
                    ? 'rgba(248, 81, 73, 0.5)'
                    : 'rgba(210, 153, 34, 0.5)',
                bgcolor:
                  storageStatus.level === 'critical'
                    ? 'rgba(248, 81, 73, 0.12)'
                    : 'rgba(210, 153, 34, 0.12)',
                color: '#f0f6fc',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <strong>Storage:</strong> Disk is {storageStatus.used_percent}%
              full on <code>{storageStatus.checked_path}</code>
              {storageStatus.free_bytes != null && (
                <>
                  {' '}
                  (
                  {(storageStatus.free_bytes / 1024 ** 3).toFixed(1)} GiB
                  free).{' '}
                </>
              )}
              {storageStatus.level === 'critical'
                ? 'Free space or add capacity soon.'
                : 'Plan cleanup, retention, or a larger volume.'}
            </Alert>
          )}
        <Box sx={{ p: { xs: 2, md: 4 }, position: 'relative', zIndex: 1, flex: 1, maxWidth: '100%', overflowX: 'hidden' }}>{children}</Box>
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 3,
            borderTop: '1px solid rgba(48, 54, 61, 0.5)',
            position: 'relative',
            zIndex: 1,
            '& a': { color: '#58a6ff' },
          }}
        >
          <LegalFooterLinks dense sx={{ color: '#8b949e' }} />
        </Box>
      </Box>

      {/* Enhanced Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
            border: '1px solid rgba(48, 54, 61, 0.5)',
            borderRadius: 3,
            mt: 1,
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            minWidth: 200,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(48, 54, 61, 0.5)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user?.profile_image || null}
              sx={{
                width: 40,
                height: 40,
                background: user?.profile_image
                  ? 'transparent'
                  : 'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {user?.first_name?.charAt(0)?.toUpperCase() ||
                user?.username?.charAt(0)?.toUpperCase() ||
                'U'}
            </Avatar>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#f0f6fc' }}
              >
                {user?.username || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8b949e' }}>
                {user?.email || 'user@example.com'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <MenuItem
          onClick={() => {
            navigate('/settings');
            handleProfileMenuClose();
          }}
          sx={{
            color: '#f0f6fc',
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(88, 166, 255, 0.08)',
            },
          }}
        >
          <ListItemIcon>
            <SettingsIcon sx={{ color: '#8b949e' }} />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(48, 54, 61, 0.5)', mx: 1 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            color: '#f0f6fc',
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(218, 54, 51, 0.08)',
            },
          }}
        >
          <ListItemIcon>
            <Logout sx={{ color: '#da3633' }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Chatbot />

      {/* Search Dialog */}
      <SearchDialog open={searchOpen} onClose={handleSearchClose} />

      {/* Notifications Dialog */}
      <NotificationsDialog
        open={notificationsOpen}
        onClose={handleNotificationsClose}
      />
    </Box>
  );
}
